import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Planbook } from "@shared/models/planbook"
import planbookChangeCoverAction from "cc-frontend/lib/actions/planbook/PLANBOOK_CHANGE_COVER"
import PLANBOOK_CHANGE_TITLE from "cc-frontend/lib/actions/planbook/PLANBOOK_CHANGE_TITLE"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Filestack from "cc-frontend/services/filestack"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import {
  gradientBackgrounds,
  imageBackgrounds,
  initialPatternForColor,
  parseCover,
  serializeCover,
  solidColorBackgrounds,
} from "cc-frontend/utils/planbook-covers"
import { startsWith } from "lodash-es"
import styles from "./title-background.module.css"

interface Signature {
  Args: {
    planbook: Planbook
    isEditingFlow: boolean
  }
}

export default class TitleBackgroundComponent extends Component<Signature> {
  readonly styles = styles

  @service declare dispatcher: Dispatcher
  @service declare session: Session
  @service declare filestack: Filestack
  @service declare modalManager: ModalManager

  @tracked showPatternMenu = false
  @tracked showCcProOptions = false
  @tracked picker

  gradientBackgrounds = gradientBackgrounds
  solidCoverBackgrounds = solidColorBackgrounds
  imageBackgrounds = imageBackgrounds

  /**
   * For image covers loaded from Imgix, the width to load, in pixels.
   *
   * We set it to 2x the preview size to save bandwidth (& load time) while providing
   * some extra pixels for the hover animation.
   */
  imageLoadWidth = 192

  onAttachFiles!: Function
  uploadHasStarted = false

  @computed("args.planbook.attributes.cover")
  get hasCustomBackground() {
    return startsWith(this.args.planbook.attributes.cover || "", "http")
  }

  @computed("args.planbook.attributes.cover")
  get selectedCoverBackground() {
    return parseCover(this.args.planbook.attributes.cover)[0]
  }

  @computed("args.planbook.attributes.cover")
  get selectedCoverPattern() {
    return parseCover(this.args.planbook.attributes.cover)[1]
  }

  @action
  selectPattern(newPattern: string | null) {
    this.togglePatternMenu()

    const [oldBackground, _oldPattern] = parseCover(this.args.planbook.attributes.cover)
    const newCover = serializeCover(oldBackground, newPattern)

    this.dispatcher.dispatchPreparedAction(
      planbookChangeCoverAction({
        planbookId: this.args.planbook.id,
        cover: newCover,
        originalCover: this.args.planbook.attributes.cover,
        backgroundType: "color",
      })
    )
  }

  @action
  selectBackground(
    newBackground: string,
    backgroundType: "unsplash" | "filestack" | "image" | "color"
  ) {
    const isFullUrl = newBackground.startsWith("http")

    let pattern: string | null = null
    if (!isFullUrl && backgroundType === "color") {
      const [_oldBackground, oldPattern] = parseCover(this.args.planbook.attributes.cover)
      pattern = oldPattern || initialPatternForColor
    }
    const newCover = serializeCover(newBackground, pattern)

    this.dispatcher.dispatchPreparedAction(
      planbookChangeCoverAction({
        planbookId: this.args.planbook.id,
        cover: newCover,
        originalCover: this.args.planbook.attributes.cover,
        backgroundType,
      })
    )
  }

  @action
  updateTitle(title: string) {
    this.dispatcher.dispatchAction(PLANBOOK_CHANGE_TITLE, {
      originalTitle: this.args.planbook.attributes.title,
      title: title,
      planbookId: this.args.planbook.id,
    })
  }

  @action
  togglePatternMenu() {
    this.showPatternMenu = !this.showPatternMenu
  }
  @action
  toggleCcProOptions() {
    this.showCcProOptions = !this.showCcProOptions
  }

  _closingModalPromise: Promise<null> | null = null

  @action
  openUnsplashModal() {
    const open = () => {
      this.modalManager.openModal(
        "planner/modals/unsplash-modal",
        {
          planbook: this.args.planbook,
          selectBackground: this.selectBackground,
        },
        {
          onClose: (promise) => {
            this._closingModalPromise = promise
          },
        }
      )
    }

    if (this._closingModalPromise) {
      this._closingModalPromise.then(() => open())
    } else {
      open()
    }
  }

  @action
  openUploadPicker() {
    if (!this.picker) {
      this.picker = this.filestack.client.picker({
        maxFiles: 1,
        accept: "image/*",
        onUploadDone: (res) => {
          let file = res.filesUploaded[0]
          if (!file) return
          this.selectBackground(file.url, "filestack")
          this.uploadHasStarted = false
        },
        onFileUploadStarted: () => {
          this.uploadHasStarted = true
        },
      })
    }
    this.picker.open()
  }

  @action
  willDestroy() {
    if (this.uploadHasStarted) return
    this.picker?.close()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Planbook::TitleBackground": typeof TitleBackgroundComponent
  }
}
